import { useTrans } from '@/shared/hooks/useTranslation';
import { cn } from '@/shared/utils';
import { Button } from '@/view/shared/Button';
import { Select } from '@/view/shared/Select';
import { DateField } from '@/view/shared/form/DateField';
import type { DynamicFormProps } from '@/view/shared/form/DynamicForm.props';
import { Input } from '@/view/shared/form/Input';
import { Textarea } from '@/view/shared/form/Textarea';
import { toDateValue } from '@/view/shared/form/dateValue';
import type { FormFieldConfigProps, FormFieldView } from '@/viewmodel/shared/form';
import React from 'react';

interface ConfigFieldProps {
  readonly field: FormFieldConfigProps;
  readonly state: FormFieldView;
}

function ConfigTextarea({ field, state }: ConfigFieldProps) {
  return (
    <Textarea
      name={field.fieldName}
      value={state.value}
      isValid={state.isValid}
      onChange={state.change}
      label={field.label}
      placeholder={field.placeholder}
      errorMessage={field.errorMessage}
      description={field.description}
      classNames={{
        container: field.classNames?.container,
        textarea: field.classNames?.textarea,
        label: field.classNames?.label,
        error: field.classNames?.error,
        description: field.classNames?.description,
      }}
    />
  );
}

function ConfigInput({ field, state }: ConfigFieldProps) {
  return (
    <Input
      name={field.fieldName}
      value={state.value}
      isValid={state.isValid}
      onChange={state.change}
      errorMessage={state.errorMessage || field.errorMessage}
      label={field.label}
      placeholder={field.placeholder}
      type={field.type}
      description={field.description}
      classNames={field.classNames}
      leftIcon={field.leftIcon}
      tooltip={field.tooltip}
      labelSuffix={field.labelSuffix}
    />
  );
}

export function DynamicForm({
  form,
  onCancel,
  submitButtonText,
  cancelButtonText,
  contentBelowFields,
  renderAfterField,
  classNames,
  isSubmitting = false,
}: Readonly<DynamicFormProps>) {
  const { t } = useTrans();

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    await form.submit();
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={cn('flex w-full flex-col gap-4 flex-1', classNames?.form)}
    >
      <div className={cn('space-y-4 flex-1', classNames?.fieldsContainer)}>
        {form.fields.map((field) => (
          <React.Fragment key={field.fieldName}>
            {field.type === 'divider' ? (
              <p
                className={cn(
                  'text-overline text-muted-foreground pt-1',
                  field.classNames?.container,
                )}
              >
                {field.label}
              </p>
            ) : field.type === 'select' ? (
              <Select
                value={form.rawValueOf(field.fieldName)}
                onChange={(selectValue) =>
                  form.setValue(field.fieldName, selectValue, selectValue !== '')
                }
                label={field.label}
                options={field.options || []}
                placeholder={field.placeholder}
                errorMessage={field.errorMessage}
                description={field.description}
                descriptionAbove={field.descriptionAbove}
                classNames={field.classNames}
                hasError={form.isInvalid(field.fieldName)}
              />
            ) : field.type === 'date' ? (
              <DateField
                name={field.fieldName}
                value={toDateValue(form.stringValueOf(field.fieldName))}
                onChange={(date) =>
                  form.setValue(field.fieldName, date ? date.toString() : '', true)
                }
                hasError={
                  form.isInvalid(field.fieldName) && Boolean(form.stringValueOf(field.fieldName))
                }
                label={field.label}
                description={field.description}
                errorMessage={field.errorMessage}
                labelSuffix={field.labelSuffix}
                classNames={{
                  container: field.classNames?.container,
                  input: field.classNames?.input,
                  label: field.classNames?.label,
                  error: field.classNames?.error,
                  description: field.classNames?.description,
                }}
              />
            ) : field.type === 'textarea' ? (
              <ConfigTextarea
                field={field}
                state={form.fieldOf(field.fieldName, field.regex || /.*/)}
              />
            ) : (
              <ConfigInput
                field={field}
                state={form.fieldOf(field.fieldName, field.regex || /.*/)}
              />
            )}
            {renderAfterField?.[field.fieldName]}
          </React.Fragment>
        ))}
      </div>
      {contentBelowFields}
      <div className={cn('mt-auto pt-3 flex flex-col gap-1.5', classNames?.buttonsContainer)}>
        <Button
          variant="primary"
          disabled={!form.isFormValid || isSubmitting}
          isLoading={isSubmitting}
          className={cn('w-full', classNames?.submitButton)}
          type="submit"
        >
          {submitButtonText || t('common.actions.submit') || 'Submit'}
        </Button>
        {onCancel && (
          <Button
            variant="ghost"
            onClick={onCancel}
            type="button"
            className={cn('w-full', classNames?.cancelButton)}
          >
            {cancelButtonText || t('common.actions.cancel')}
          </Button>
        )}
      </div>
    </form>
  );
}
